// ─────────────────────────────────────────────────────────────────
// Glass-i — Post Inventory Inward page (Stage 6P.5).
//
// Single page that handles BOTH inward sources:
//   /erp/inventory/purchase-inward/grn/:grnId       → GRN-source posting
//   /erp/inventory/purchase-inward/bill/:purchaseBillId → Bill-source posting
//
// Mode is detected from URL params (grnId vs purchaseBillId) and from
// the route pathname. On mount the page calls the matching preview
// endpoint (inventoryApi.previewFromGrn / previewFromPurchaseBill) and
// renders an item-type-aware preview table with colour-coded action
// chips (POST / SKIP_NON_STOCKABLE / DUPLICATE_BLOCKED). On submit it
// posts to the matching post endpoint (inventoryApi.postFromGrn /
// postFromPurchaseBill) and renders an AntD Result panel with the
// posted / skipped / duplicate counts returned by the server.
//
// Style: mirrors the GoodsReceiptFormPage / PurchaseBillFormPage shell
// density (slate-50 header strip, sticky toolbar, card sections, 22px
// table rows). Tailwind handles layout; AntD owns primitives.
//
// Backend (`inventoryPosting.service.previewInventoryInwardFrom* /
// postInventoryInwardFrom*`) remains the source of truth for ALL
// posting math, duplicate-block decisions and ledger row creation. The
// preview rows are display-only; the operator confirms, the server
// recomputes and decides.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Result,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  InboxOutlined,
  LinkOutlined,
  StopOutlined
} from '@ant-design/icons';

import { inventoryApi } from './inventoryApi';
import {
  GLASS_FAMILY_TYPES,
  PROFILE_FAMILY_TYPES,
  itemTypeColor,
  itemTypeLabel,
  normalizeInventoryPostingPreview,
  summarizePostingPreview
} from './inventoryHelpers';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Local constants — mirror the classifier-action vocabulary emitted
// by `inventoryPosting.service.buildPreviewFrom{Grn,PurchaseBill}`.
// These are display-only labels; the canonical strings live in the
// backend `inventory.constants.js`.
// ─────────────────────────────────────────────────────────────────
const ACTION_POST                = 'POST';
const ACTION_SKIP_NON_STOCKABLE  = 'SKIP_NON_STOCKABLE';
const ACTION_DUPLICATE_BLOCKED   = 'DUPLICATE_BLOCKED';

const ACTION_LABELS = {
  [ACTION_POST]:               'Will Post',
  [ACTION_SKIP_NON_STOCKABLE]: 'Skip (Non-Stockable)',
  [ACTION_DUPLICATE_BLOCKED]:  'Duplicate (Blocked)'
};

const ACTION_COLORS = {
  [ACTION_POST]:               'success',
  [ACTION_SKIP_NON_STOCKABLE]: 'default',
  [ACTION_DUPLICATE_BLOCKED]:  'error'
};

const ACTION_ICONS = {
  [ACTION_POST]:               <CheckCircleOutlined />,
  [ACTION_SKIP_NON_STOCKABLE]: <StopOutlined />,
  [ACTION_DUPLICATE_BLOCKED]:  <CloseCircleOutlined />
};

// Item-type family sets — used to drive the per-row column variants.
const GLASS_TYPE_SET   = new Set(GLASS_FAMILY_TYPES);
const PROFILE_TYPE_SET = new Set(PROFILE_FAMILY_TYPES);

// Mode markers — kept symbolic so the rest of the file reads clearly.
const MODE_GRN  = 'GRN';
const MODE_BILL = 'PURCHASE_BILL';

// ─────────────────────────────────────────────────────────────────
// Tolerant numeric formatter — preview numbers arrive as JS numbers
// from the API but we render zero as a dim '—' to keep the cells
// scannable for HARDWARE/ACCESSORY rows.
// ─────────────────────────────────────────────────────────────────
function fmtNum(v, decimals = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// ─────────────────────────────────────────────────────────────────
// Detect mode from the URL params + path. Param naming is fixed by
// the route registration:
//   /erp/inventory/purchase-inward/grn/:grnId
//   /erp/inventory/purchase-inward/bill/:purchaseBillId
// We trust the param key first; pathname is a fallback.
// ─────────────────────────────────────────────────────────────────
function resolveMode(params, pathname) {
  if (params && params.grnId) return MODE_GRN;
  if (params && params.purchaseBillId) return MODE_BILL;
  if (typeof pathname === 'string') {
    if (pathname.indexOf('/purchase-inward/grn/')  !== -1) return MODE_GRN;
    if (pathname.indexOf('/purchase-inward/bill/') !== -1) return MODE_BILL;
  }
  return null;
}

function resolveSourceId(mode, params) {
  if (!params) return null;
  if (mode === MODE_GRN)  return params.grnId || null;
  if (mode === MODE_BILL) return params.purchaseBillId || null;
  return null;
}

// ─────────────────────────────────────────────────────────────────
// Status-tag colour — the source-document status comes back on the
// preview header (GRN.status or purchase_bill.status). We use a small
// palette so it visually matches the GRN / PB pages.
// ─────────────────────────────────────────────────────────────────
const SOURCE_STATUS_COLORS = {
  // GRN
  DRAFT:              'default',
  RECEIVED:           'processing',
  INSPECTED:          'cyan',
  ACCEPTED:           'success',
  PARTIALLY_ACCEPTED: 'gold',
  REJECTED:           'error',
  CANCELLED:          'default',
  // Purchase Bill
  POSTING_PENDING:    'processing',
  APPROVED:           'success',
  POSTED:             'success'
};

function sourceStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return SOURCE_STATUS_COLORS[key] || 'default';
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function PostInventoryInwardPage() {
  const params   = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode     = useMemo(
    () => resolveMode(params, location.pathname),
    [params, location.pathname]
  );
  const sourceId = useMemo(() => resolveSourceId(mode, params), [mode, params]);

  // Preview state — normalized to a stable shape so the table never
  // chokes on a missing field. `null` means "not loaded yet".
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Post-result state — when set we render the AntD Result panel in
  // place of the action toolbar. `null` = no post attempted yet.
  const [postResult,  setPostResult]  = useState(null);
  const [postError,   setPostError]   = useState(null);
  const [posting,     setPosting]     = useState(false);

  // ── Initial preview fetch ────────────────────────────────────
  const loadPreview = useCallback(async () => {
    if (!mode || !sourceId) {
      setLoadError('Missing source identifier in URL.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const raw = mode === MODE_GRN
        ? await inventoryApi.previewFromGrn(sourceId)
        : await inventoryApi.previewFromPurchaseBill(sourceId);
      setPreview(normalizeInventoryPostingPreview(raw));
    } catch (e) {
      const msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load inward preview.';
      setLoadError(msg);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [mode, sourceId]);

  useEffect(() => {
    // Reset when source changes (e.g. operator navigates between two
    // inward pages without unmounting).
    setPostResult(null);
    setPostError(null);
    loadPreview();
  }, [loadPreview]);

  // ── Submit handler ───────────────────────────────────────────
  const handlePost = useCallback(async () => {
    if (!mode || !sourceId) return;
    setPosting(true);
    setPostError(null);
    try {
      const result = mode === MODE_GRN
        ? await inventoryApi.postFromGrn(sourceId, {})
        : await inventoryApi.postFromPurchaseBill(sourceId, {});
      // Result shape:
      //   { posted: [...], skipped: [...], duplicates: [...], movementNos: [...] }
      const postedCount    = Array.isArray(result && result.posted)     ? result.posted.length     : 0;
      const skippedCount   = Array.isArray(result && result.skipped)    ? result.skipped.length    : 0;
      const duplicateCount = Array.isArray(result && result.duplicates) ? result.duplicates.length : 0;
      const movementNos    = Array.isArray(result && result.movementNos) ? result.movementNos      : [];
      setPostResult({
        posted:     postedCount,
        skipped:    skippedCount,
        duplicates: duplicateCount,
        movementNos
      });
      if (postedCount > 0) {
        message.success(`Posted ${postedCount} inventory movement${postedCount === 1 ? '' : 's'}.`);
      } else {
        message.warning('No inventory movements were posted (all lines skipped or blocked).');
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to post inventory inward.';
      setPostError(msg);
      message.error(msg);
    } finally {
      setPosting(false);
    }
  }, [mode, sourceId]);

  // ── Derived counts — summary cards + button-disable guard ────
  const summary = useMemo(() => summarizePostingPreview(preview), [preview]);
  const canPost = !posting && !postResult && (summary.postable || 0) > 0;

  // ── Item-type-aware preview table columns ────────────────────
  const columns = useMemo(() => [
    {
      title: '#',
      width: 50,
      align: 'center',
      render: (_, __, idx) => idx + 1
    },
    {
      title: 'Item',
      dataIndex: 'itemName',
      width: 280,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.itemName || '(unnamed)'}</span>
          {row.itemType ? (
            <Tag color={itemTypeColor(row.itemType)} style={{ marginInlineEnd: 0 }}>
              {itemTypeLabel(row.itemType)}
            </Tag>
          ) : null}
        </Space>
      )
    },
    {
      title: 'W (mm)',
      dataIndex: 'widthMm',
      width: 90,
      align: 'right',
      render: (_, row) =>
        GLASS_TYPE_SET.has(row.itemType)
          ? fmtNum(row.candidate && row.candidate.widthMm, 2)
          : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'H (mm)',
      dataIndex: 'heightMm',
      width: 90,
      align: 'right',
      render: (_, row) =>
        GLASS_TYPE_SET.has(row.itemType)
          ? fmtNum(row.candidate && row.candidate.heightMm, 2)
          : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'Length',
      dataIndex: 'lengthValue',
      width: 100,
      align: 'right',
      render: (_, row) => {
        if (PROFILE_TYPE_SET.has(row.itemType)) {
          const lv = row.candidate && row.candidate.lengthValue;
          const uom = row.candidate && row.candidate.lengthUom;
          return (
            <Space size={2}>
              <span>{fmtNum(lv, 2)}</span>
              {uom ? <Text type="secondary" style={{ fontSize: 11 }}>{uom}</Text> : null}
            </Space>
          );
        }
        return <span style={{ color: '#cbd5e1' }}>—</span>;
      }
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Space size={2}>
          <span>{fmtNum(row.qty, 3)}</span>
          {row.uom ? <Text type="secondary" style={{ fontSize: 11 }}>{row.uom}</Text> : null}
        </Space>
      )
    },
    {
      title: 'SQMT',
      dataIndex: 'sqmt',
      width: 90,
      align: 'right',
      render: (_, row) =>
        GLASS_TYPE_SET.has(row.itemType)
          ? fmtNum(row.sqmt, 4)
          : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'SQFT',
      dataIndex: 'sqft',
      width: 90,
      align: 'right',
      render: (_, row) =>
        GLASS_TYPE_SET.has(row.itemType)
          ? fmtNum(row.sqft, 4)
          : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'Total Length',
      dataIndex: 'totalLength',
      width: 110,
      align: 'right',
      render: (_, row) =>
        PROFILE_TYPE_SET.has(row.itemType)
          ? fmtNum(row.totalLength, 4)
          : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 200,
      render: (action, row) => {
        const color = ACTION_COLORS[action] || 'default';
        const label = ACTION_LABELS[action] || action || 'Unknown';
        const icon  = ACTION_ICONS[action]  || null;
        const blockedHint =
          action === ACTION_DUPLICATE_BLOCKED && row.existingLedgerId
            ? `Already posted as ledger ${row.existingLedgerId}`
            : null;
        const skipHint =
          action === ACTION_SKIP_NON_STOCKABLE
            ? `Item type ${itemTypeLabel(row.itemType)} is non-stockable`
            : null;
        const tag = (
          <Tag color={color} icon={icon} style={{ marginInlineEnd: 0 }}>
            {label}
          </Tag>
        );
        if (blockedHint || skipHint) {
          return <Tooltip title={blockedHint || skipHint}>{tag}</Tooltip>;
        }
        return tag;
      }
    }
  ], []);

  // ── Row class — colour-code blocked / skipped rows ───────────
  const rowClassName = useCallback((row) => {
    if (!row || !row.action) return '';
    if (row.action === ACTION_DUPLICATE_BLOCKED) return 'inv-inward-row--blocked';
    if (row.action === ACTION_SKIP_NON_STOCKABLE) return 'inv-inward-row--skipped';
    return '';
  }, []);

  // ── Header card content — source identity + status ──────────
  const headerItems = useMemo(() => {
    const h = (preview && preview.sourceHeader) || {};
    const isGrn = mode === MODE_GRN;
    const out = [];
    if (isGrn) {
      out.push({ label: 'GRN No',      value: h.grnNo  || '—' });
      out.push({ label: 'GRN Date',    value: h.grnDate || '—' });
    } else {
      out.push({ label: 'Bill No',     value: h.billNo  || '—' });
      out.push({ label: 'Bill Date',   value: h.billDate || '—' });
      if (h.supplierInvoiceNo) {
        out.push({ label: 'Supplier Invoice', value: h.supplierInvoiceNo });
      }
    }
    out.push({ label: 'Supplier', value: h.supplierName || '—' });
    if (h.poNo) {
      out.push({ label: 'PO No', value: h.poNo });
    }
    if (!isGrn && h.grnNo) {
      out.push({ label: 'GRN No', value: h.grnNo });
    }
    if (isGrn && h.warehouseName) {
      out.push({ label: 'Warehouse', value: h.warehouseName });
    }
    return out;
  }, [preview, mode]);

  // ── Render: error / loading / loaded ─────────────────────────
  const titleText = mode === MODE_GRN
    ? 'Post Inventory Inward (from GRN)'
    : mode === MODE_BILL
      ? 'Post Inventory Inward (from Purchase Bill)'
      : 'Post Inventory Inward';

  return (
    <div className="p-4">
      {/* Page head */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            <InboxOutlined style={{ marginInlineEnd: 8 }} />
            {titleText}
          </Title>
          <Text type="secondary">
            Review candidate ledger entries before posting. Backend recomputes
            on post and is the final authority.
          </Text>
        </div>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      </div>

      {/* Load error */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load preview"
          description={loadError}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadPreview}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Spin spinning={loading}>
        {/* Header card */}
        {preview && preview.sourceHeader ? (
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space>
                <LinkOutlined />
                <span>Source Document</span>
                {preview.sourceHeader.status ? (
                  <Tag color={sourceStatusColor(preview.sourceHeader.status)}>
                    {preview.sourceHeader.status}
                  </Tag>
                ) : null}
              </Space>
            }
          >
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              colon
            >
              {headerItems.map((it, idx) => (
                <Descriptions.Item key={idx} label={it.label}>
                  {it.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        ) : null}

        {/* Summary stats */}
        {preview ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Statistic
                title="Total Lines"
                value={summary.total || 0}
                prefix={<FileDoneOutlined />}
              />
              <Statistic
                title="Will Post"
                value={summary.postable || 0}
                valueStyle={{ color: '#16a34a' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic
                title="Skipped"
                value={summary.skipped || 0}
                valueStyle={{ color: '#6b7280' }}
                prefix={<StopOutlined />}
              />
              <Statistic
                title="Duplicate (Blocked)"
                value={summary.duplicates || 0}
                valueStyle={{ color: '#dc2626' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </div>
          </Card>
        ) : null}

        {/* Post result (replaces toolbar after a successful post) */}
        {postResult ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Result
              status={postResult.posted > 0 ? 'success' : 'warning'}
              title={
                postResult.posted > 0
                  ? `Posted ${postResult.posted} inventory movement${postResult.posted === 1 ? '' : 's'}.`
                  : 'Nothing was posted.'
              }
              subTitle={
                <Space direction="vertical" size={4}>
                  <span>
                    Posted: <strong>{postResult.posted}</strong>
                    {' · '}
                    Skipped: <strong>{postResult.skipped}</strong>
                    {' · '}
                    Duplicate (blocked): <strong>{postResult.duplicates}</strong>
                  </span>
                  {Array.isArray(postResult.movementNos) && postResult.movementNos.length > 0 ? (
                    <Space wrap size={4}>
                      <Text type="secondary">Movement Nos:</Text>
                      {postResult.movementNos.map((mn) => (
                        <Tag key={mn} color="blue">{mn}</Tag>
                      ))}
                    </Space>
                  ) : null}
                </Space>
              }
              extra={[
                <Button
                  key="back"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>,
                <Button
                  key="refresh"
                  type="primary"
                  onClick={() => {
                    setPostResult(null);
                    setPostError(null);
                    loadPreview();
                  }}
                >
                  Refresh Preview
                </Button>
              ]}
            />
          </Card>
        ) : null}

        {/* Post error (kept above the toolbar so the operator sees it) */}
        {postError && !postResult ? (
          <Alert
            type="error"
            showIcon
            message="Posting failed"
            description={postError}
            style={{ marginBottom: 12 }}
            closable
            onClose={() => setPostError(null)}
          />
        ) : null}

        {/* Preview table */}
        <Card
          size="small"
          title={
            <Space>
              <span>Candidate Ledger Entries</span>
              {preview && Array.isArray(preview.candidates) ? (
                <Text type="secondary">({preview.candidates.length} lines)</Text>
              ) : null}
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Table
            size="small"
            rowKey={(row, idx) =>
              (row && (row.sourceLineId || row.purchaseBillLineId || row.itemId))
              || `row-${idx}`
            }
            columns={columns}
            dataSource={(preview && preview.candidates) || []}
            pagination={false}
            rowClassName={rowClassName}
            scroll={{ x: 1100 }}
            locale={{ emptyText: loading ? ' ' : 'No candidate lines to preview.' }}
          />
        </Card>

        {/* Footer toolbar — hidden once we have a post result */}
        {!postResult ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              disabled={posting}
            >
              Back
            </Button>
            <Tooltip
              title={
                canPost
                  ? ''
                  : (summary.postable || 0) === 0
                    ? 'No postable lines — nothing to send.'
                    : 'Posting in progress…'
              }
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={posting}
                disabled={!canPost}
                onClick={handlePost}
              >
                Post to Inventory
              </Button>
            </Tooltip>
          </div>
        ) : null}
      </Spin>

      {/* Lightweight inline styles for row colouring — avoids a
          companion .css file while keeping the visual cue obvious. */}
      <style>{`
        .inv-inward-row--blocked  > td { background-color: #fef2f2 !important; }
        .inv-inward-row--blocked:hover > td { background-color: #fee2e2 !important; }
        .inv-inward-row--skipped  > td { background-color: #f8fafc !important; color: #64748b; }
        .inv-inward-row--skipped:hover > td { background-color: #f1f5f9 !important; }
      `}</style>
    </div>
  );
}
